import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Attr } from '@mode-switch/model';
import { EncDualAxisXField, EncDualAxisYField, EncField, Encoding, EncodingResult } from '@mode-vegas/vegas';
import { AccountFacade } from '@mode/accounts/contract';
import { ChartFacade, DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS, getFormatterByType } from '@mode/shared/util-js';
import { CompiledField, CompiledFormula, FormulaOperations } from '@mode/shared/util-switch';
import { ToastService } from '@mode/shared/util-toast';
import { getFormatMapKeyByField } from 'libs/charts/util-vegas/src/lib/field-format-helpers';
import { BehaviorSubject, Observable, Subject, Subscription, of, takeUntil } from 'rxjs';
import { DragEndData, DragStartData, OnDropEvent } from '../helpers/field-helpers';

@Component({
  selector: 'mode-encoding-builder-histogram',
  templateUrl: './encoding-builder-histogram.component.html',
  styleUrls: ['./encoding-builder-histogram.component.scss'],
})
export class EncodingBuilderHistogramsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() encoding!: Encoding;
  @Input() observables: any;
  @Input() chartService!: SelectedChartService;
  @Input() model!: FlamingoTypes.ModeModel;
  @Input() format!: ChartTypes.ChartFormat;
  @Input() isExploring = false;
  @Input() chartType!: ChartTypes.ChartType;

  protected destroyed$ = new Subject<void>();

  defaultColorPalettes: readonly OrgTypes.ColorPalette[] = [];
  customColorPalettes: readonly OrgTypes.ColorPalette[] = [];
  allColorPalettes: readonly OrgTypes.ColorPalette[] = [];
  currentColorPaletteToken: string | undefined;
  fieldColors: ChartTypes.FieldColorMap | undefined;
  selectedColorPalette: OrgTypes.ColorPalette | undefined;

  fieldFormats: ChartTypes.FieldFormatMap | undefined;
  binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap = new Map();
  token!: string;
  pendingBinSizeMap: { [x: string]: boolean } = {};
  queryToken!: string;

  scale: ChartTypes.ScaleFormat = {};
  activeColor: string | undefined;
  showFlyout = false;

  binSize: number | undefined;
  suggestedBinSize: string | undefined;
  dragInfo: DragFieldInfo | undefined;
  view: FlamingoTypes.ModeView | undefined;
  formula: CompiledFormula | undefined;

  columns: ReadonlyArray<EncField | EncDualAxisXField> = [];
  rows: ReadonlyArray<EncField | EncDualAxisYField> = [];

  chartSubscription: Subscription | undefined;
  viewSubscription: Subscription | undefined;

  queryToken$!: Observable<string>;
  fieldFormatMap$!: Observable<ChartTypes.FieldFormatMap>;
  vizToken$!: Observable<string>;
  view$!: Observable<FlamingoTypes.ModeView>;

  private isLoadingSubject$ = new BehaviorSubject<boolean>(true);
  isLoading$: Observable<boolean>;
  disableUseRecommended = true;

  constructor(
    private chartFacade: ChartFacade,
    private accountFacade: AccountFacade,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
    private alertService: ToastService
  ) {
    this.isLoading$ = this.isLoadingSubject$.asObservable().pipe(takeUntil(this.destroyed$));
  }

  ngOnInit(): void {
    this.queryToken$ = this.observables.queryToken$;
    this.fieldFormatMap$ = this.observables.fieldFormatMap$;
    this.view$ = this.observables.view$;
    this.viewSubscription = this.view$.subscribe((view) => (this.view = view));
    this.observables?.binSizeToFieldMap$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap) => {
        this.binSizeToFieldMap = binSizeToFieldMap;
        this.updateEncodingWithBinningInfo();
        this.getRecommendedBinSize();
      });

    this.vizToken$ = this.isExploring ? of(FLAMINGO_RESERVED_REFS.Exploration) : this.observables.token$;
    this.vizToken$.subscribe((token: string) => {
      this.token = token;
    });

    this.observables.colorPaletteToken$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((token: string) => (this.currentColorPaletteToken = token));

    this.observables.fieldColors$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fieldColors: ChartTypes.FieldColorMap) => (this.fieldColors = fieldColors));

    this.accountFacade.reportOwnerColorPalettes$.pipe(takeUntil(this.destroyed$)).subscribe((palettes) => {
      this.allColorPalettes = [...palettes].sort((a, b) => a.name.localeCompare(b.name));
      this.defaultColorPalettes = palettes.filter(
        (p: OrgTypes.ColorPalette) => p.type === 'categorical' && p.predefined
      );
      this.customColorPalettes = palettes.filter(
        (p: OrgTypes.ColorPalette) => p.type === 'categorical' && !p.predefined
      );

      this.selectedColorPalette =
        this.allColorPalettes.find((palette) => palette.type === 'categorical' && palette.isDefault) ??
        this.allColorPalettes[0];
      this.currentColorPaletteToken = this.selectedColorPalette.token;
    });

    this.observables.fieldFormats$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fieldFormats: ChartTypes.FieldFormatMap) => {
        this.fieldFormats = fieldFormats;
      });

    this.chartSubscription = this.chartFacade.dragField$.subscribe((dragInfo: DragFieldInfo) => {
      this.dragInfo = dragInfo;
    });

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('dragover', this.documentDragOver);
    });
    this.initBinSize();

    const colName = this.formula?.attrName;
    if (!colName) {
      return;
    }

    if (!this.binSizeToFieldMap.has(colName)) {
      this.isLoadingSubject$.next(true);
      this.chartService.getBinSize(this.token, colName);
    } else {
      this.isLoadingSubject$.next(true);
      this.getRecommendedBinSize();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.columns = this.buildColumns();
    this.initBinSize();

    if (changes.encoding) {
      const colName = this.formula?.attrName;
      if (!colName) {
        return;
      }

      if (!this.binSizeToFieldMap.has(colName)) {
        this.isLoadingSubject$.next(true);
        this.chartService.getBinSize(this.token, colName);
      } else {
        this.getRecommendedBinSize();
      }
    }

    if (changes.format) {
      this.getRecommendedBinSize();
    }

    if (changes.encoding && changes.encoding.currentValue) {
      this.activeColor = this.determineActiveFieldColor();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.chartSubscription?.unsubscribe();
    this.viewSubscription?.unsubscribe();
    this.document.removeEventListener('dragover', this.documentDragOver);
  }

  buildColumns() {
    if (this.encoding != null) {
      return EncodingHelpers.buildBasicColumns(this.encoding);
    }
    return [];
  }

  initBinSize() {
    const field = this.encoding.x?.[0];
    if (!field || !this.view) {
      this.binSize = undefined;
      return;
    }
    this.formula = CompiledField.fromEncField(this.view, field);
    this.binSize = this.formula.binSize || undefined;
  }

  isDragging() {
    if (this.dragInfo?.formula?.isSystem) {
      return false;
    }

    return !!this.dragInfo?.formula;
  }

  updateEncodingWithBinningInfo() {
    const colName = this.formula?.attrName;
    if (colName && this.binSizeToFieldMap.get(colName) && this.pendingBinSizeMap[colName]) {
      if (!this.binSizeToFieldMap.get(colName)?.binSize) {
        this.handleInvalidBinSize();
        delete this.pendingBinSizeMap[colName];
        return;
      }

      const defBinSize = this.binSizeToFieldMap.get(colName)?.binSize ?? 1;
      this.binSize = defBinSize;
      this.handleEncodingUpdate();
      delete this.pendingBinSizeMap[colName];
    }
  }

  handleInvalidBinSize() {
    this.alertService.error(`Field doesn't have sufficient data to calculate binSize`);
    this.handleRemoveField();
  }

  getRecommendedBinSize() {
    const colName = this.formula?.attrName;
    if (colName) {
      const binSize = this.binSizeToFieldMap.get(colName)?.binSize ?? 1;
      this.suggestedBinSize = this.formatBinSize(binSize);
      this.disableUseRecommended = this.binSize === binSize;
      this.isLoadingSubject$.next(false);
    }
  }

  formatBinSize(binSize: number) {
    if (this.formula) {
      const formatKey = getFormatMapKeyByField(this.formula.toEncField());

      const axisFormat = this.fieldFormats?.[formatKey]?.[ChartTypes.FieldFormatTypes.Axis];
      const scaleFormat = axisFormat?.[ChartTypes.FieldAxisTypes.Scale] ?? ({} as ChartTypes.ScaleFormat);
      const scaleFormatConfig = scaleFormat[ChartTypes.FieldAxisTypes.Default];

      const headerFormat = this.fieldFormats?.[formatKey]?.[ChartTypes.FieldFormatTypes.Header];
      const headerDefaultFormatConfig = headerFormat?.[ChartTypes.FieldHeaderTypes.Default];

      const formatConfig = scaleFormatConfig ?? headerDefaultFormatConfig;

      const formattedValue = formatConfig
        ? getFormatterByType(formatConfig.type)(binSize, formatConfig)
        : String(binSize);

      return formattedValue;
    }
  }

  removeXAndYFieldsFromEncoding(encoding: Encoding) {
    const fieldIds = [encoding.x?.[0]?.id ?? '', encoding.y?.[0]?.id ?? ''];
    if (fieldIds.length) {
      return EncodingHelpers.removeFieldsFromEncoding(
        encoding,
        fieldIds.filter((id) => !!id)
      );
    }
    return EncodingHelpers.loadEncoding(encoding);
  }

  handleBinSizeUpdate($event: { binSize: number; useRecommended: boolean }) {
    const colName = this.formula?.attrName;
    if (!colName) {
      return;
    }

    if ($event.useRecommended) {
      this.pendingBinSizeMap[colName] = true;
      this.chartService.getBinSize(this.token, colName);
      return;
    }

    if (this.binSize === $event.binSize || !this.view || !$event.binSize) {
      return;
    }
    this.binSize = $event.binSize;
    this.handleEncodingUpdate();
  }

  handleEncodingUpdate() {
    if (!this.binSize || !this.formula) {
      return;
    }
    let newEncoding: EncodingResult = this.removeXAndYFieldsFromEncoding(this.encoding);
    const binSource = FormulaOperations.bin(this.formula, this.binSize);
    newEncoding = EncodingHelpers.generateHistogramQCEncoding(newEncoding.encoding, this.formula, binSource);
    if (!newEncoding) {
      return;
    }
    if (this.activeColor && this.selectedColorPalette) {
      newEncoding = EncodingHelpers.updateMarkColor(newEncoding.encoding, this.activeColor);
      this.chartService.dispatchEncodingAndFormatUpdate(
        newEncoding,
        undefined,
        undefined,
        undefined,
        undefined,
        this.selectedColorPalette.token
      );
      return;
    }
    this.chartService.dispatchEncodingUpdate(newEncoding);
  }

  onDrop($event: OnDropEvent) {
    const { attr } = $event;
    const formula = attr ? attr : this.dragInfo?.formula;
    this.handleFieldDrop(formula);
  }

  onFieldDrop() {
    if (this.dragInfo == null) {
      return;
    }
    const formula = this.dragInfo.formula;
    this.handleFieldDrop(formula);
  }

  handleFieldDrop(formula: CompiledFormula | undefined) {
    this.chartFacade.endFieldDrag();

    if (!formula) {
      return;
    }

    if (formula.varType !== Attr.Type.Continuous) {
      this.alertService.error(`Discrete dimensions cannot be used for this chart type`);
      return;
    }

    const colName = formula.attrName;
    if (!colName) {
      return;
    }

    this.pendingBinSizeMap[colName] = true;
    this.formula = formula;
    /**
     * Need to add normal continuous field to encoding to make it visible in encoding
     *  builder. Otherwise field will be visible only after bin size is fetched.
     */
    this.encoding = EncodingHelpers.addBasicFieldToEncoding(
      this.removeXAndYFieldsFromEncoding(this.encoding)?.encoding,
      formula,
      'column',
      undefined,
      EncodingHelpers.ZoneType.Continuous
    )?.encoding;
    this.columns = this.buildColumns();

    this.suggestedBinSize = undefined;
    this.disableUseRecommended = true;
    this.isLoadingSubject$.next(true);
    this.chartService.getBinSize(this.token, colName);
  }

  onDragStart({ field, source }: DragStartData) {
    if (this.view && this.columns.some((f) => f.id === field.id)) {
      const compiledField = CompiledField.fromEncField(this.view, field as EncField);
      this.chartFacade.startFieldDrag(compiledField, source);
    }
  }

  onDragEnd({ field }: DragEndData) {
    if (this.dragInfo == null) {
      return;
    }
    if (this.dragInfo.formula) {
      this.removeField({ $field: field });
      this.chartFacade.endFieldDrag();
    }
  }

  removeField($event: { $field: EncField }) {
    const { $field } = $event;
    if ($field.id == null) {
      return;
    }
    this.handleRemoveField();
  }

  handleRemoveField() {
    this.chartService.dispatchEncodingUpdate(this.removeXAndYFieldsFromEncoding(this.encoding));
    this.binSize = undefined;
    this.formula = undefined;
  }

  documentDragOver(event: DragEvent) {
    event.preventDefault();
  }

  determineActiveFieldColor() {
    let color: string | undefined;
    if (this.encoding.marks.channel === '*') {
      color = this.encoding.marks.properties.color.value;
    } else if (this.encoding.marks.series.length > 0) {
      color = this.encoding.marks.series[0].properties.color.value;
    }
    if (color == null) {
      // fallback to org default palette
      color = this.format?.colors?.custom[0];
    }
    return color;
  }

  onColorSwatchFlyoutUpdated(colorSelected: string, selectedPalette: OrgTypes.ColorPalette) {
    const newEncoding = EncodingHelpers.updateMarkColor(this.encoding, colorSelected);
    this.selectedColorPalette = selectedPalette;
    this.activeColor = colorSelected;

    this.chartService.dispatchEncodingAndFormatUpdate(
      newEncoding,
      undefined,
      undefined,
      undefined,
      undefined,
      selectedPalette.token
    );
  }
}
